import React, { useState, useEffect } from 'react';
import { InventoryItem, InventoryCopy, listInventoryCopies, updateInventoryCopy, updatePreferredPriceOverride, removeCopy, RemoveCopyRequest } from '../../api/inventory';
import { gameDisplayName } from '../../lib/tcgplayerListing';
import { isMagicProductLine } from '../../lib/inventoryNav';
import { ManapoolSalesChart } from '../pricing/ManapoolSalesChart';
import { PolicySignalsPanel } from '../pricing/PolicySignalsPanel';
import { colors, spacing, typographyFlat, borderRadius } from '../../styles/tokens';
import { formatInstant, formatInstantDate } from '../../utils/datetime';
import { ManaSymbol } from '../card/ManaSymbol';
import { CardImage } from '../card/CardImage';
import { SetSymbol } from '../card/SetSymbol';
import {
  CONDITIONS,
  LANGUAGES,
  FinishValue,
  finishFromCopy,
  finishFromItem,
  finishLabel,
  finishesForProductLine,
  withCurrentFinish,
  displayCondition,
  formConditionMatchesCopy,
} from '../../lib/cardVariantOptions';

interface CopyEditState {
  cost: string;
  location: string;
  condition: string;
  finish: FinishValue;
  language: string;
}

interface InventoryDrawerProps {
  item: InventoryItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const InventoryDrawer: React.FC<InventoryDrawerProps> = ({
  item,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [copies, setCopies] = useState<InventoryCopy[]>([]);
  const [isLoadingCopies, setIsLoadingCopies] = useState<boolean>(false);
  const [savingCopies, setSavingCopies] = useState<Set<string>>(new Set());
  const [copyEdits, setCopyEdits] = useState<Record<string, CopyEditState>>({});
  const [preferredPriceOverride, setPreferredPriceOverride] = useState<string>('');
  const [isSavingPreferredPrice, setIsSavingPreferredPrice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  // Remove copy modal state
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);
  const [copyToRemove, setCopyToRemove] = useState<InventoryCopy | null>(null);
  const [removeReason, setRemoveReason] = useState<RemoveCopyRequest['reason']>('UNFOUND');
  const [trackAsShrink, setTrackAsShrink] = useState<boolean>(true);
  const [removeNotes, setRemoveNotes] = useState<string>('');
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  useEffect(() => {
    if (item) {
      setError(null);
      setIsCardFlipped(false);
      setPreferredPriceOverride(item.preferred_price_override != null ? item.preferred_price_override.toFixed(2) : '');
    }
  }, [item]);

  useEffect(() => {
    if (!item) return;

    const fetchCopies = async () => {
      setIsLoadingCopies(true);
      setError(null);

      try {
        const printing = finishFromItem(item);
        const data = await listInventoryCopies({
          product_id: item.variant_id,
          condition: item.condition || undefined,
          is_foil: item.foil || false,
          language: item.language || undefined,
          printing,
        });
        setCopies(data);
        setCopyEdits({});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load copies');
      } finally {
        setIsLoadingCopies(false);
      }
    };

    fetchCopies();
  }, [item]);

  const handleClose = () => {
    if (savingCopies.size === 0) {
      onClose();
    }
  };

  const getCopyEditState = (copy: InventoryCopy): CopyEditState => {
    const edits = copyEdits[copy.id];
    if (edits) return edits;
    return {
      cost: copy.acquisition_cost != null ? copy.acquisition_cost.toFixed(2) : '',
      location: copy.location_bin || '',
      condition: displayCondition(copy.condition),
      finish: finishFromCopy(copy.is_foil, copy.printing),
      language: copy.language || 'EN',
    };
  };

  const handleCopyChange = (copyId: string, field: keyof CopyEditState, value: string) => {
    setCopyEdits((prev) => {
      const current = prev[copyId];
      const copy = copies.find((entry) => entry.id === copyId);
      const base = current ?? (copy ? getCopyEditState(copy) : {
        cost: '',
        location: '',
        condition: 'NM',
        finish: 'normal' as FinishValue,
        language: 'EN',
      });
      return {
        ...prev,
        [copyId]: {
          ...base,
          [field]: value,
        },
      };
    });
  };

  const handleCopySave = async (copy: InventoryCopy) => {
    const edits = getCopyEditState(copy);

    const payload: {
      acquisition_cost?: number;
      location?: string;
      condition?: string;
      language?: string;
      printing?: string;
    } = {};

    const parsedCost = edits.cost.trim() === '' ? null : Number(edits.cost);
    if (parsedCost !== null && !Number.isNaN(parsedCost) && parsedCost !== copy.acquisition_cost) {
      payload.acquisition_cost = parsedCost;
    }

    const nextLocation = edits.location.trim();
    if (nextLocation !== (copy.location_bin || '')) {
      payload.location = nextLocation;
    }

    if (!formConditionMatchesCopy(edits.condition, copy.condition)) {
      payload.condition = edits.condition;
    }

    const nextLanguage = edits.language || 'EN';
    if (nextLanguage !== (copy.language || 'EN')) {
      payload.language = nextLanguage;
    }

    const nextPrinting = edits.finish;
    const currentPrinting = finishFromCopy(copy.is_foil, copy.printing);
    if (nextPrinting !== currentPrinting) {
      payload.printing = nextPrinting;
    }

    if (Object.keys(payload).length === 0) return;

    const variantChanged = Boolean(payload.condition || payload.language || payload.printing);

    setSavingCopies((prev) => new Set(prev).add(copy.id));
    setError(null);

    try {
      const updated = await updateInventoryCopy(copy.id, payload);
      setCopyEdits((prev) => {
        const next = { ...prev };
        delete next[copy.id];
        return next;
      });

      if (variantChanged) {
        setCopies((prev) => prev.filter((entry) => entry.id !== copy.id));
      } else {
        setCopies((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update copy');
    } finally {
      setSavingCopies((prev) => {
        const next = new Set(prev);
        next.delete(copy.id);
        return next;
      });
    }
  };

  const handlePreferredPriceSave = async () => {
    if (!item) return;

    const trimmedValue = preferredPriceOverride.trim();
    const nextOverride = trimmedValue === '' ? null : Number(trimmedValue);
    if (nextOverride !== null && Number.isNaN(nextOverride)) {
      setError('Preferred price override must be a valid number');
      return;
    }

    setIsSavingPreferredPrice(true);
    setError(null);

    try {
      await updatePreferredPriceOverride(item.variant_id, {
        condition: item.condition ?? 'NM',
        is_foil: item.foil ?? false,
        language: item.language ?? 'EN',
        printing: finishFromItem(item),
        preferred_price_override: nextOverride,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update preferred price override');
    } finally {
      setIsSavingPreferredPrice(false);
    }
  };

  const handleRemoveClick = (copy: InventoryCopy) => {
    setCopyToRemove(copy);
    setRemoveModalOpen(true);
    setRemoveReason('UNFOUND');
    setTrackAsShrink(true);
    setRemoveNotes('');
    setError(null);
  };

  const handleRemoveCancel = () => {
    setRemoveModalOpen(false);
    setCopyToRemove(null);
    setRemoveReason('UNFOUND');
    setTrackAsShrink(true);
    setRemoveNotes('');
  };

  const handleRemoveConfirm = async () => {
    if (!copyToRemove) return;

    setIsRemoving(true);
    setError(null);

    try {
      const request: RemoveCopyRequest = {
        reason: removeReason,
        track_as_shrink: trackAsShrink,
        notes: removeNotes.trim() || undefined,
      };

      await removeCopy(copyToRemove.id, request);
      
      // Remove the copy from local state
      setCopies((prev) => prev.filter((c) => c.id !== copyToRemove.id));
      
      // Clear any pending edits for this copy
      setCopyEdits((prev) => {
        const next = { ...prev };
        delete next[copyToRemove.id];
        return next;
      });

      // Close modal
      setRemoveModalOpen(false);
      setCopyToRemove(null);
      
      // Notify parent to refresh data
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove copy');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveReasonChange = (reason: RemoveCopyRequest['reason']) => {
    setRemoveReason(reason);
    // Auto-check shrink tracking for UNFOUND, DAMAGED, LOST
    const shrinkReasons: RemoveCopyRequest['reason'][] = ['UNFOUND', 'DAMAGED', 'LOST'];
    setTrackAsShrink(shrinkReasons.includes(reason));
  };

  if (!isOpen || !item) return null;

  const magicChrome = isMagicProductLine(item.product_line);
  const showCardDetails = Boolean(
    item.rarity
    || item.type_line
    || item.image_url
    || (item.game_metadata && item.game_metadata.length > 0)
    || (magicChrome && (
      item.mana_cost
      || item.oracle_text
      || item.power
      || item.toughness
      || item.keywords?.length
      || item.edhrec_rank
      || item.edhrec_saltiness != null
      || item.is_reserved
      || item.is_game_changer
      || item.leadership_skills
      || item.legalities
      || item.scryfall_id
    ))
  );

  return (
    <>
      {/* Backdrop */}
      <div
        style={styles.backdrop}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div style={styles.drawer}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{item.product_name || 'Unknown Card'}</h2>
            <p style={styles.subtitle}>
              {magicChrome ? (
                <SetSymbol setCode={item.set_code} setName={item.set_name} size="sm" />
              ) : (
                <span>{item.set_name || 'Unknown Set'}</span>
              )}
              {item.collector_number ? ` #${item.collector_number}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={styles.closeButton}
            aria-label="Close drawer"
            disabled={savingCopies.size > 0}
          >
            ✕
          </button>
        </div>

        <div style={styles.form}>
          {/* Variant Info */}
          <div style={styles.variantInfo}>
            <>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Game:</span>
                <span style={styles.infoValue}>{gameDisplayName(item.product_line)}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Condition:</span>
                <span style={styles.infoValue}>{item.condition}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Finish:</span>
                <span style={styles.infoValue}>
                  {finishLabel(Boolean(item.foil), finishFromItem(item))}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Language:</span>
                <span style={styles.infoValue}>{item.language}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Quantity:</span>
                <span style={styles.infoValue}>{item.quantity}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Location:</span>
                <span style={styles.infoValue}>{item.location || '—'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Updated:</span>
                <span style={styles.infoValue}>
                  {formatInstant(item.updated_at)}
                </span>
              </div>
              {item.collector_number && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Collector #:</span>
                  <span style={styles.infoValue}>{item.collector_number}</span>
                </div>
              )}
            </>
          </div>

          {/* Card Details (MTGJSON for Magic; shared TCGCSV game_metadata otherwise) */}
          {showCardDetails && (
            <div style={styles.cardDetails}>
              <h3 style={styles.sectionTitle}>Card Details</h3>

              {/* Card Image. Scryfall ids are Magic-only; other games use catalog image_url. */}
              {(item.image_url || (magicChrome && item.scryfall_id)) && (
                <div style={{ marginBottom: spacing.lg }}>
                  <CardImage
                    scryfallId={magicChrome ? item.scryfall_id : undefined}
                    reverseScryfallId={magicChrome ? item.reverse_scryfall_id : undefined}
                    imageUrl={item.image_url}
                    reverseImageUrl={item.reverse_image_url}
                    isDualFaced={!!item.is_dual_faced}
                    isFlipped={isCardFlipped}
                    cardName={item.product_name}
                    size="md"
                    onFlip={(e) => {
                      e.stopPropagation();
                      setIsCardFlipped((prev) => !prev);
                    }}
                  />
                </div>
              )}
              
              {item.rarity && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Rarity:</span>
                  <span style={styles.infoValue}>{item.rarity}</span>
                </div>
              )}
              
              {item.type_line && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Type:</span>
                  <span style={styles.infoValue}>{item.type_line}</span>
                </div>
              )}

              {item.game_metadata?.map((field) => {
                if (field.label === 'Card Type' && item.type_line) {
                  return null;
                }
                return (
                  <div key={`${field.key}-${field.label}`} style={styles.infoRow}>
                    <span style={styles.infoLabel}>{field.label}:</span>
                    <span style={styles.infoValue}>{field.value}</span>
                  </div>
                );
              })}
              
              {magicChrome && item.mana_cost && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Mana Cost:</span>
                  <div style={{ ...styles.infoValue, display: 'flex', alignItems: 'center' }}>
                    <ManaSymbol manaCost={item.mana_cost} size="md" />
                  </div>
                </div>
              )}
              
              {magicChrome && (item.power || item.toughness) && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>P/T:</span>
                  <span style={styles.infoValue}>{item.power || '—'} / {item.toughness || '—'}</span>
                </div>
              )}
              
              {magicChrome && item.keywords && item.keywords.length > 0 && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Keywords:</span>
                  <span style={styles.infoValue}>{item.keywords.join(', ')}</span>
                </div>
              )}

              {magicChrome && item.edhrec_rank && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>EDHREC Rank:</span>
                  <span style={styles.infoValue}>#{item.edhrec_rank}</span>
                </div>
              )}

              {magicChrome && item.edhrec_saltiness !== undefined && item.edhrec_saltiness !== null && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>EDHREC Saltiness:</span>
                  <span style={styles.infoValue}>{Number(item.edhrec_saltiness).toFixed(2)}</span>
                </div>
              )}

              {magicChrome && item.is_game_changer && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Game Changer:</span>
                  <span style={styles.infoValue}>Yes</span>
                </div>
              )}

              {magicChrome && item.is_reserved && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Reserved List:</span>
                  <span style={styles.infoValue}>Yes</span>
                </div>
              )}

              {magicChrome && item.leadership_skills && Object.keys(item.leadership_skills).length > 0 && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Leadership Skills:</span>
                  <span style={styles.infoValue}>
                    {Object.entries(item.leadership_skills)
                      .filter(([, value]) => value)
                      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
                      .join(', ')}
                  </span>
                </div>
              )}

              {magicChrome && item.legalities && Object.keys(item.legalities).length > 0 && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Format Legality:</span>
                  <span style={styles.infoValue}>
                    {Object.entries(item.legalities)
                      .filter(([, status]) => status === 'Legal')
                      .map(([format]) => format.charAt(0).toUpperCase() + format.slice(1))
                      .join(', ')}
                  </span>
                </div>
              )}
              
              {magicChrome && item.oracle_text && (
                <div style={styles.oracleText}>
                  <span style={styles.infoLabel}>Oracle Text:</span>
                  <p style={styles.oracleTextContent}>{item.oracle_text}</p>
                </div>
              )}
            </div>
          )}

          <div style={styles.pricingSection}>
            <h3 style={styles.sectionTitle}>Sell pricing</h3>
            <PolicySignalsPanel
              policyPrice={item.policy_price}
              debug={item.policy_price_debug}
            />
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Effective Preferred Price:</span>
              <span style={styles.infoValue}>{item.preferred_price != null ? `$${item.preferred_price.toFixed(2)}` : '—'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Calculated Price:</span>
              <span style={styles.infoValue}>{item.calculated_preferred_price != null ? `$${item.calculated_preferred_price.toFixed(2)}` : '—'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Manapool Low:</span>
              <span style={styles.infoValue}>{item.manapool_low != null ? `$${item.manapool_low.toFixed(2)}` : '—'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Manapool Listed:</span>
              <span style={styles.infoValue}>{item.manapool_listing_price != null ? `$${item.manapool_listing_price.toFixed(2)}` : '—'}</span>
            </div>
            <ManapoolSalesChart sales={item.manapool_recent_sales} />
            <div style={styles.overrideRow}>
              <input
                type="text"
                inputMode="decimal"
                value={preferredPriceOverride}
                onChange={(e) => setPreferredPriceOverride(e.target.value)}
                placeholder="Leave blank to clear override"
                style={styles.copyInput}
                disabled={isSavingPreferredPrice}
                aria-label="Preferred price override"
              />
              <button
                type="button"
                style={styles.copySaveButton}
                onClick={handlePreferredPriceSave}
                disabled={isSavingPreferredPrice}
              >
                {isSavingPreferredPrice ? 'Saving...' : 'Save Override'}
              </button>
            </div>
          </div>

          {/* Copies */}
          <div style={styles.copiesSection}>
            <h3 style={styles.sectionTitle}>Copies</h3>
            {isLoadingCopies ? (
              <div style={styles.loadingText}>Loading copies...</div>
            ) : copies.length === 0 ? (
              <div style={styles.loadingText}>No copies found.</div>
            ) : (
              <div style={styles.copiesList}>
                <div style={styles.copyHeaderRow}>
                  <span style={styles.copyHeaderCell}>Copy</span>
                  <span style={styles.copyHeaderCell}>Acquired</span>
                  <span style={styles.copyHeaderCell}>Cond</span>
                  <span style={styles.copyHeaderCell}>Finish</span>
                  <span style={styles.copyHeaderCell}>Lang</span>
                  <span style={styles.copyHeaderCell}>Cost</span>
                  <span style={styles.copyHeaderCell}>Location</span>
                  <span style={styles.copyHeaderCell} />
                  <span style={styles.copyHeaderCell} />
                </div>
                {copies.map((copy) => {
                  const edits = getCopyEditState(copy);
                  const isSaving = savingCopies.has(copy.id);

                  return (
                    <div key={copy.id} style={styles.copyRow}>
                      <div style={styles.copyValue}>#{copy.id.slice(0, 8)}</div>
                      <div style={styles.copyValue}>
                        {copy.acquired_at
                          ? formatInstantDate(copy.acquired_at)
                          : '—'}
                      </div>
                      <select
                        value={edits.condition}
                        onChange={(e) => handleCopyChange(copy.id, 'condition', e.target.value)}
                        style={styles.copySelect}
                        disabled={isSaving}
                        aria-label="Condition"
                      >
                        {CONDITIONS.map((cond) => (
                          <option key={cond.value} value={cond.value}>{cond.value}</option>
                        ))}
                      </select>
                      <select
                        value={edits.finish}
                        onChange={(e) => handleCopyChange(copy.id, 'finish', e.target.value)}
                        style={styles.copySelect}
                        disabled={isSaving}
                        aria-label="Finish"
                      >
                        {withCurrentFinish(finishesForProductLine(item.product_line), edits.finish).map((finishOption) => (
                          <option key={finishOption.value} value={finishOption.value}>{finishOption.label}</option>
                        ))}
                      </select>
                      <select
                        value={edits.language}
                        onChange={(e) => handleCopyChange(copy.id, 'language', e.target.value)}
                        style={styles.copySelect}
                        disabled={isSaving}
                        aria-label="Language"
                      >
                        {LANGUAGES.map((item) => (
                          <option key={item.value} value={item.value}>{item.value}</option>
                        ))}
                      </select>
                      <input
                        id={`copy-cost-${copy.id}`}
                        type="text"
                        inputMode="decimal"
                        value={edits.cost}
                        onChange={(e) => handleCopyChange(copy.id, 'cost', e.target.value)}
                        style={styles.copyInput}
                        disabled={isSaving}
                        aria-label="Cost"
                      />
                      <input
                        id={`copy-location-${copy.id}`}
                        type="text"
                        value={edits.location}
                        onChange={(e) => handleCopyChange(copy.id, 'location', e.target.value)}
                        style={styles.copyInput}
                        disabled={isSaving}
                        aria-label="Location"
                      />
                      <button
                        type="button"
                        style={styles.copySaveButton}
                        onClick={() => handleCopySave(copy)}
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        style={styles.copyRemoveButton}
                        onClick={() => handleRemoveClick(copy)}
                        disabled={isSaving || savingCopies.size > 0}
                        aria-label="Remove copy"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <div style={styles.error} role="alert">
              {error}
            </div>
          )}

          <div style={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={savingCopies.size > 0}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Remove Copy Modal */}
      {removeModalOpen && copyToRemove && (
        <>
          <div
            style={styles.modalBackdrop}
            onClick={handleRemoveCancel}
            aria-hidden="true"
          />
          <div style={styles.modal} role="dialog" aria-labelledby="remove-modal-title">
            <h3 id="remove-modal-title" style={styles.modalTitle}>Remove Copy</h3>
            <p style={styles.modalDescription}>
              Remove copy #{copyToRemove.id.slice(0, 8)} from inventory.
            </p>

            <div style={styles.modalSection}>
              <label htmlFor="remove-reason" style={styles.modalLabel}>
                Reason *
              </label>
              <select
                id="remove-reason"
                value={removeReason}
                onChange={(e) => handleRemoveReasonChange(e.target.value as RemoveCopyRequest['reason'])}
                style={styles.modalSelect}
                disabled={isRemoving}
              >
                <optgroup label="Shrink (Tax Write-off)">
                  <option value="UNFOUND">Unfound (Missing from Order)</option>
                  <option value="DAMAGED">Damaged</option>
                  <option value="LOST">Lost</option>
                </optgroup>
                <optgroup label="Non-Shrink Removal">
                  <option value="PROMOTIONAL">Promotional Giveaway</option>
                  <option value="DONATION">Donation</option>
                  <option value="SAMPLE">Sample/Display</option>
                  <option value="ERROR_CORRECTION">Error Correction</option>
                  <option value="OTHER">Other</option>
                </optgroup>
              </select>
            </div>

            <div style={styles.modalSection}>
              <label style={styles.modalCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={trackAsShrink}
                  onChange={(e) => setTrackAsShrink(e.target.checked)}
                  disabled={isRemoving}
                  style={styles.modalCheckbox}
                />
                Track as shrink (tax write-off)
              </label>
              <p style={styles.modalHint}>
                {trackAsShrink 
                  ? 'This removal will be recorded in shrink tracking for tax purposes.'
                  : 'This removal will NOT be tracked as shrink.'}
              </p>
            </div>

            <div style={styles.modalSection}>
              <label htmlFor="remove-notes" style={styles.modalLabel}>
                Notes (optional)
              </label>
              <textarea
                id="remove-notes"
                value={removeNotes}
                onChange={(e) => setRemoveNotes(e.target.value)}
                placeholder="Add notes about this removal..."
                style={styles.modalTextarea}
                disabled={isRemoving}
                rows={3}
              />
            </div>

            {error && (
              <div style={styles.modalError} role="alert">
                {error}
              </div>
            )}

            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleRemoveCancel}
                style={styles.modalCancelButton}
                disabled={isRemoving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRemoveConfirm}
                style={styles.modalConfirmButton}
                disabled={isRemoving}
              >
                {isRemoving ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `${colors.text}80`,
    zIndex: 1000,
  },
  drawer: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: '680px',
    maxWidth: '90vw',
    backgroundColor: colors.surface,
    boxShadow: `-4px 0 12px ${colors.text}26`,
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column' as const,
    overflowY: 'auto' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    fontSize: typographyFlat.fontSize.xl,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.xs} 0 0`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  closeButton: {
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.xl,
    color: colors.textSecondary,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    lineHeight: 1,
    transition: 'color 0.2s ease',
    ':hover': {
      color: colors.text,
    },
  } as React.CSSProperties,
  form: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.lg,
  },
  variantInfo: {
    padding: spacing.md,
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${spacing.xs} 0`,
    fontSize: typographyFlat.fontSize.sm,
  },
  infoLabel: {
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
  },
  infoValue: {
    color: colors.text,
  },
  pricingSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
  },
  overrideRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: spacing.sm,
    alignItems: 'center',
  },
  copiesSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  copiesList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  copyHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '72px 72px 52px 72px 52px 56px 1fr auto auto',
    gap: spacing.xs,
    alignItems: 'center',
    padding: `0 ${spacing.xs}`,
  },
  copyHeaderCell: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  copyRow: {
    display: 'grid',
    gridTemplateColumns: '72px 72px 52px 72px 52px 56px 1fr auto auto',
    gap: spacing.xs,
    alignItems: 'center',
    padding: spacing.xs,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.backgroundAlt,
  },
  copyValue: {
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  copyInput: {
    padding: '2px 6px',
    borderRadius: borderRadius.xs,
    border: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.sm,
    width: '100%',
  },
  copySelect: {
    padding: '2px 4px',
    borderRadius: borderRadius.xs,
    border: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.xs,
    width: '100%',
    backgroundColor: colors.surface,
    color: colors.text,
  },
  copySaveButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.xs,
    border: `1px solid ${colors.primary}`,
    backgroundColor: 'transparent',
    color: colors.primary,
    cursor: 'pointer',
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  loadingText: {
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  label: {
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  required: {
    color: colors.error,
  },
  input: {
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    ':focus': {
      borderColor: colors.primary,
      boxShadow: `0 0 0 2px ${colors.primary}33`,
    },
  } as React.CSSProperties,
  error: {
    padding: spacing.md,
    backgroundColor: `${colors.error}11`,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    color: colors.error,
    fontSize: typographyFlat.fontSize.sm,
  },
  actions: {
    display: 'flex',
    gap: spacing.md,
    justifyContent: 'flex-end',
    marginTop: spacing.md,
  },
  cancelButton: {
    padding: `${spacing.sm} ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: colors.background,
    },
  } as React.CSSProperties,
  saveButton: {
    padding: `${spacing.sm} ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.white,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: colors.primaryDark,
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  } as React.CSSProperties,
  cardDetails: {
    padding: spacing.md,
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    marginTop: spacing.md,
  },
  sectionTitle: {
    margin: `0 0 ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    paddingBottom: spacing.xs,
    borderBottom: `1px solid ${colors.border}`,
  },
  oracleText: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTop: `1px solid ${colors.border}`,
  },
  oracleTextContent: {
    margin: 0,
    fontSize: typographyFlat.fontSize.sm,
    lineHeight: 1.5,
    color: colors.text,
    whiteSpace: 'pre-wrap' as const,
  },
  copyRemoveButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.xs,
    border: `1px solid ${colors.error}`,
    backgroundColor: 'transparent',
    color: colors.error,
    cursor: 'pointer',
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.medium,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: `${colors.error}11`,
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  } as React.CSSProperties,
  modalBackdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: `${colors.text}66`,
    zIndex: 1100,
  },
  modal: {
    position: 'fixed' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    padding: spacing.xl,
    boxShadow: `0 8px 24px ${colors.text}33`,
    zIndex: 1101,
    minWidth: '400px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    margin: 0,
    fontSize: typographyFlat.fontSize.lg,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  modalDescription: {
    margin: 0,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.lg,
  },
  modalSection: {
    marginBottom: spacing.lg,
  },
  modalLabel: {
    display: 'block',
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  modalSelect: {
    width: '100%',
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    backgroundColor: colors.background,
    color: colors.text,
  },
  modalCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    cursor: 'pointer',
  },
  modalCheckbox: {
    cursor: 'pointer',
  },
  modalHint: {
    margin: `${spacing.xs} 0 0 ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    fontStyle: 'italic' as const,
  },
  modalTextarea: {
    width: '100%',
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    backgroundColor: colors.background,
    color: colors.text,
  },
  modalError: {
    padding: spacing.sm,
    backgroundColor: `${colors.error}11`,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    color: colors.error,
    fontSize: typographyFlat.fontSize.sm,
    marginBottom: spacing.md,
  },
  modalActions: {
    display: 'flex',
    gap: spacing.md,
    justifyContent: 'flex-end',
  },
  modalCancelButton: {
    padding: `${spacing.sm} ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: colors.background,
    },
  } as React.CSSProperties,
  modalConfirmButton: {
    padding: `${spacing.sm} ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.white,
    backgroundColor: colors.error,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#c0392b',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  } as React.CSSProperties,
};
